import { useState, useRef } from 'react';

export function useMapControls() {
  const [showApproximate, setShowApproximate] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showZipBoundaries, setShowZipBoundaries] = useState(false);
  const [colorMode, setColorMode] = useState('activity');
  const [multiThreshold, setMultiThreshold] = useState(4);
  const [mapZoom, setMapZoom] = useState(12);
  const [selectedYears, setSelectedYears] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [pinSize, setPinSize] = useState(() => {
    const saved = localStorage.getItem('mapPinSize');
    return saved ? Number(saved) : 6;
  });
  const mapRef = useRef(null);

  const [visibleCategories, setVisibleCategories] = useState({
    single: true, repeat: true, multi23: true, multi4: true,
    democrat: true, republican: true, mixed: true, unknown: true,
  });

  const toggleCategory = (key) => {
    setVisibleCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isFiltered = Object.values(visibleCategories).some(v => !v);

  const resetFilters = () => setVisibleCategories({
    single: true, repeat: true, multi23: true, multi4: true,
    democrat: true, republican: true, mixed: true, unknown: true,
  });

  const getCategoryKey = (location) => {
    if (colorMode === 'party') {
      if (location.party === 'democrat') return 'democrat';
      if (location.party === 'republican') return 'republican';
      if (location.party === 'mixed') return 'mixed';
      return 'unknown';
    }
    if (location.is_multi_donor && location.unique_donors >= multiThreshold) return 'multi4';
    if (location.is_multi_donor) return 'multi23';
    if (location.is_cluster) return 'repeat';
    return 'single';
  };

  const getMarkerColor = (location) => {
    if (location.is_approximate) return '#9ca3af';
    if (colorMode === 'party') {
      if (location.party === 'democrat') return '#2563eb';
      if (location.party === 'republican') return '#dc2626';
      if (location.party === 'mixed') return '#7c3aed';
      return '#9ca3af';
    }
    if (location.is_multi_donor && location.unique_donors >= multiThreshold) return '#dc2626';
    if (location.is_multi_donor) return '#f59e0b';
    if (location.is_cluster) return '#8b5cf6';
    return '#3b82f6';
  };

  const handlePinSizeChange = (val) => {
    setPinSize(val);
    localStorage.setItem('mapPinSize', String(val));
    if (mapRef.current) {
      mapRef.current.eachLayer((layer) => {
        if (layer.setRadius && layer._baseR) {
          layer._baseR = val;
          const zoom = mapRef.current.getZoom();
          const scale = Math.pow(1.5, (zoom - 14));
          const clamped = Math.max(0.8, Math.min(3, scale));
          layer.setRadius(val * clamped);
        }
      });
    }
  };

  const toggleYear = (year) => {
    setSelectedYears(prev => {
      if (prev.includes(year)) {
        return prev.filter(y => y !== year);
      }
      return [...prev, year].sort((a, b) => a - b);
    });
  };

  return {
    showApproximate, setShowApproximate,
    isFullscreen, setIsFullscreen,
    showZipBoundaries, setShowZipBoundaries,
    colorMode, setColorMode,
    multiThreshold, setMultiThreshold,
    mapZoom, setMapZoom,
    pinSize,
    selectedYears, setSelectedYears,
    availableYears, setAvailableYears,
    toggleYear,
    mapRef,
    visibleCategories,
    toggleCategory,
    isFiltered,
    resetFilters,
    getCategoryKey,
    getMarkerColor,
    handlePinSizeChange,
  };
}
